<template>
  <section ref="sportContainer" class="sport-container" data-t="sportContainer">
    <SportHeader
      v-if="renderPartTree.length"
      id="sport-header"
      :key="`sport-${sportTree.sport.id}`"
      :title="sportTree.sport.name"
      :filters="sportTree.marketsConfig"
      :icon-name="sportIcon"
      :mini="isCollapsed"
      @toogle-sport="toogleSport"
      @update-filters="handleFiltersUpdate"
    />
    <SportListSkeleton v-else />
    <div
      v-show="!isCollapsed"
      class="sport-container-group"
      data-t="sportContainerGroup"
    >
      <div
        v-for="(tournament, tournamentIndex) in renderPartTree"
        :key="`${tournament.tournament.id}-${tournamentIndex}`"
        class="sport-container-tournament"
        data-t="tournamentContainer"
      >
        <TournamentHeader
          v-if="tournament.sportEvents.length"
          :tournament="tournament.tournament"
          :category="tournament.category"
          :live-count="getTournametLiveCount(tournament.tournament.entityId)"
          @toogle-tournament="
            handleTournamentHeaderClick(
              tournament.tournament.id,
              tournamentIndex,
            )
          "
        />
        <Transition>
          <div
            v-show="
              isVisibleEventItem(tournament.tournament.id, tournamentIndex)
            "
            class="sport-container-items"
          >
            <EventItem
              v-for="event in tournament.sportEvents"
              :key="event.id"
              :event="event"
              :markets-filters="filtersArray"
            />
          </div>
        </Transition>
      </div>
    </div>
    <div ref="endOfTheList" />
  </section>
</template>

<script setup lang="ts">
import type { FilterConfig } from 'markets-store/marketsbook/types'
import { useInfiniteScroll } from '@st/use/composables'
import { useSportList } from '../../composables'
import TournamentHeader from '../TournamentHeader/TournamentHeader.vue'
import SportHeader from '../SportHeader/SportHeader.vue'
import EventItem from '../EventItem/EventItem.vue'
import type { RenderFullTree } from '../../types'
import { useSportListItem } from './useSportListItem'

interface Props {
  sportTree: RenderFullTree
  perPage?: number
}

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  perPage: 5,
})
const { sportTree } = toRefs(props)
const {
  routeCategory,
  routeTournament,
  handleTournamentHeaderClick,
  isVisibleEventItem,
  sportIcon,
} = useSportListItem(sportTree)

const endOfTheList = ref<HTMLDivElement>()
const sportContainer = ref<HTMLDivElement>()
const isCollapsed = ref(false)
const { canLoadMore, loadMore, renderPartTree } = useSportList({
  perPage: props.perPage,
  disabled: isCollapsed,
  renderFullTree: sportTree,
  routeCategory,
  routeTournament,
})

const isDisabledObserver = computed(
  () => isCollapsed.value || !canLoadMore.value,
)
const filtersArray = ref<(FilterConfig | null)[]>([])

function toogleSport() {
  const sportContainerElement = unref(sportContainer)

  const sizes = sportContainerElement?.getBoundingClientRect()
  const sizeY = sizes?.y ?? 0
  if (sizeY < 0) {
    const scrollOffset = 100
    const needToScroll = window.scrollY - Math.abs(sizeY) - scrollOffset
    window.scrollTo({
      top: needToScroll,
      left: 0,
    })
  }
  isCollapsed.value = !isCollapsed.value
}
function handleFiltersUpdate(filters: (FilterConfig | null)[]) {
  filtersArray.value = filters
}

useInfiniteScroll({
  el: endOfTheList,
  loadMore: () => (canLoadMore.value ? loadMore() : undefined),
  disabledObserver: isDisabledObserver,
  options: { rootMargin: '200px', threshold: 0 },
})

const route = useRoute()
watch(
  () => route.path,
  () => {
    isCollapsed.value = false
  },
)

const { liveEventsMap } = useSportMenu()
function getTournametLiveCount(entityId: string) {
  return liveEventsMap.value[entityId]?.size ?? 0
}
</script>

<style scoped>
.sport-container {
  position: relative;
  z-index: 5;

  margin-bottom: var(--spacing-200);

  background: var(--background-primary);
  border-radius: var(--border-radius-150);
}

.sport-container-group {
  position: relative;
  padding: 0 var(--spacing-100);
  padding-bottom: var(--spacing-025);
}

.no-data {
  margin: 0;
  text-align: center;
  background-color: var(--background-base);
}

.v-enter-active,
.v-leave-active {
  transition:
    opacity 0.25s ease-in-out,
    transform 0.3s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  transform: translateY(10px);
  opacity: 0;
}
</style>
